import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { kHtmlParserCore } from "../../../Admin/kReusableCoreTS/kHtmlParserCore";

// AppFrameStyle - takes EVERY inline style out of a contact page before the app shows it, so the page
// runs under the app's Content-Security-Policy with NO 'unsafe-inline' for styles (2026-09-27).
//
// ==================================================================================================
// !! ⛔ WHY THE APP REWRITES THE PAGE INSTEAD OF ALLOWING INLINE STYLES
// ==================================================================================================
//
// The contact page is a `blob:` frame (`AppMain.Frame`), and a `blob:` document INHERITS the app's policy
// (measured: `csp.e2e.mjs`). Pages come from the subscriber's Dropbox and carry a `<style>` block
// generated per build by the service (`DossierHtml`) - and pages written by OLDER builds carry older CSS
// plus `style="..."` attributes (the avatar disc, the 1.1.0.8 reaction icons). No hash can cover them all,
// and a policy cannot be changed after the app has loaded. So, before the frame's `blob:` is made:
//   1. every `<style>` block is lifted out and becomes a stylesheet of its own, linked where it stood;
//   2. every `style="..."` attribute becomes `data-wac-s="N"` plus a rule `[data-wac-s="N"]{... !important}`
//      in one more stylesheet, linked before `</head>` (`!important`, because an inline style beats every
//      normal author rule and the rewritten page must look exactly as before);
//   3. each stylesheet is handed to the frame as a `blob:` URL of our own (`AppFrameSheet`) - a `<link>` in
//      the head blocks rendering, so there is no unstyled flash.
// The policy then needs only `style-src 'self' blob:`. ⭐ A `blob:` URL can only be MINTED by script of
// this origin, so injected markup cannot bring its own stylesheet (a `data:` source would let it).
//
// ⚠ IT REWRITES STRINGS IN PLACE, NOT A PARSED TREE. Everything outside the style blocks and the style
// attributes is copied byte for byte - message text, the `wac-data` JSON block, comments. A re-serialised
// DOM would normalise markup the page's own readers (`DossierHtml.PriorIn`, the tests' goldens) depend on.
// ⚠ NOT `DOMParser`: measured in Chrome, parsing into an inert document still REPORTS a violation for
// every `<style>` and `style=` in it - the parse itself would break "zero violations".
// ⚠ A style value that could break out of its rule (`{`, `}`, `<`, `\`, a line break, an unclosed quote
// or parenthesis) is DROPPED, not carried - exactly what the browser would have done under the policy.
// ⛔ Raw-text elements (`script`, `textarea`, `title` ...) are copied untouched: their text is not markup.

/**
 * A page with its styles taken out: `Pieces[0]` + link to `Sheets[0]` + `Pieces[1]` + ... + `Pieces[n]`.
 * ⭐ EACH `<style>` BLOCK STAYS ITS OWN SHEET, linked where it stood: one block with an unclosed comment or
 * brace then damages only itself, exactly as it did inline. The rules made from `style` attributes are the
 * last sheet, linked just before `</head>`.
 */
export class AppFrameSplit {
    public Pieces: string[] = [];
    public Sheets: string[] = [];
    /** How many `style` attributes were rewritten (the tests, the e2e count). */
    public Attrs: number = 0;
    /** How many `<style>` blocks were lifted out. */
    public Blocks: number = 0;

    /**
     * The page unchanged, as one piece with no sheet - the answer when nothing could be scanned.
     * DOCUMENTED EXCEPTION: pure construction. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static Whole = (Html: string): AppFrameSplit => {
        const Out: AppFrameSplit = new AppFrameSplit();
        Out.Pieces = [nz(Html)];
        return Out;
    };
}

/** One pass over one page. A class, so the scan's state is not threaded through every call. */
export class AppFrameScan {
    private Text: string = "";
    private Parts: string[] = [];
    /** Where in `Parts` each lifted `<style>` block's link goes (an empty placeholder part). */
    private Slots: number[] = [];
    /** Where the attribute rules' link goes: `</head>`, or -1 until one is seen. */
    private RulesAt: number = -1;
    private Sheets: string[] = [];
    private Values: string[] = [];
    private Rules: string[] = [];
    private Attrs: number = 0;

    /** Raw-text and escapable-raw-text elements: their content is copied, never scanned as markup. */
    private static readonly RawText: string[] = ["script", "textarea", "title", "noscript", "noembed", "noframes", "xmp", "iframe"];

    /** The app's own stylesheet for the frame (reactions, task actions), in the same sheet as the rules. */
    private Extra: string = "";

    public constructor(Html: string, Extra: string) {
        this.Text = nz(Html);
        this.Extra = nz(Extra);
    }

    public Run = (): AppFrameSplit => {
        if (getCoreUtils().KErrorMode === true) { return AppFrameSplit.Whole(this.Text); }
        try {
            let At: number = 0;
            while (At < this.Text.length) {
                const Tag: number = kHtmlParserCore.NextTagStart(this.Text, At);
                if (Tag < 0) { this.Parts.push(this.Text.substring(At)); break; }
                this.Parts.push(this.Text.substring(At, Tag));
                At = this.Markup(Tag);
            }
            return this.Result();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Run");
            return AppFrameSplit.Whole(this.Text);
        }
    };

    /** One `<...>` at `Tag`: copied, rewritten or lifted out. Answers where the scan goes on. */
    private Markup = (Tag: number): number => {
        if (getCoreUtils().KErrorMode === true) { return this.Text.length; }
        try {
            if (this.Text.startsWith("<!--", Tag)) { return this.CopyTo(Tag, this.Text.indexOf("-->", Tag + 4), 3); }
            const Next: string = this.Text.charAt(Tag + 1);
            if (Next === "/" || Next === "!" || Next === "?") { return this.Close(Tag); }
            const End: number = AppFrameStyle.TagEnd(this.Text, Tag);
            if (End < 0) { this.Parts.push(this.Text.substring(Tag)); return this.Text.length; }
            const Name: string = AppFrameStyle.NameOf(this.Text.substring(Tag + 1, End));
            if (Name === "style") { return this.Lift(End + 1); }
            this.Parts.push(this.Rewrite(this.Text.substring(Tag, End + 1)));
            if (AppFrameScan.RawText.indexOf(Name) >= 0) { return this.Raw(Name, End + 1); }
            return End + 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Markup");
            return this.Text.length;
        }
    };

    /** Copies `Text[From .. Found + Tail)`, or the rest when `Found` is -1. */
    private CopyTo = (From: number, Found: number, Tail: number): number => {
        if (getCoreUtils().KErrorMode === true) { return this.Text.length; }
        try {
            let Stop: number = this.Text.length;
            if (Found >= 0) { Stop = Found + Tail; }
            this.Parts.push(this.Text.substring(From, Stop));
            return Stop;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.CopyTo");
            return this.Text.length;
        }
    };

    /** An end tag, doctype or declaration - copied. ⭐ The first `</head>` is where the attribute rules go. */
    private Close = (Tag: number): number => {
        if (getCoreUtils().KErrorMode === true) { return this.Text.length; }
        try {
            if (this.RulesAt < 0 && this.Text.substring(Tag, Tag + 6).toLowerCase() === "</head") { this.RulesAt = this.Mark(); }
            return this.CopyTo(Tag, this.Text.indexOf(">", Tag), 1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Close");
            return this.Text.length;
        }
    };

    /** Reserves a place for one stylesheet link (an empty part) and answers its index in `Parts`. */
    private Mark = (): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            this.Parts.push("");
            return this.Parts.length - 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Mark");
            return -1;
        }
    };

    /** A `<style>` block, its start tag already read up to `From`: its text joins the sheet; nothing stays. */
    private Lift = (From: number): number => {
        if (getCoreUtils().KErrorMode === true) { return this.Text.length; }
        try {
            const Close: number = AppFrameStyle.CloseOf(this.Text, "style", From);
            let Stop: number = this.Text.length;
            let Body: string = this.Text.substring(From);
            if (Close >= 0) {
                Body = this.Text.substring(From, Close);
                const Gt: number = this.Text.indexOf(">", Close);
                if (Gt >= 0) { Stop = Gt + 1; }
            }
            this.Sheets.push(Body);
            this.Slots.push(this.Mark());
            return Stop;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Lift");
            return this.Text.length;
        }
    };

    /** The text of a raw-text element, copied as it is; its end tag is read by the loop. */
    private Raw = (Name: string, From: number): number => {
        if (getCoreUtils().KErrorMode === true) { return this.Text.length; }
        try {
            const Close: number = AppFrameStyle.CloseOf(this.Text, Name, From);
            if (Close < 0) { this.Parts.push(this.Text.substring(From)); return this.Text.length; }
            this.Parts.push(this.Text.substring(From, Close));
            return Close;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Raw");
            return this.Text.length;
        }
    };

    /**
     * One start tag with every `style` attribute taken out; the FIRST one's value becomes a rule (`Keep`),
     * as the browser uses only the first. ⚠ A second `style` on one tag is still removed, with no rule.
     */
    private Rewrite = (TagText: string, Keep: boolean = true): string => {
        if (getCoreUtils().KErrorMode === true) { return TagText; }
        try {
            const Found: number[] = AppFrameStyle.StyleAttr(TagText);
            if (Found.length === 0) { return TagText; }
            let Rule: string = "";
            if (Keep === true) { Rule = AppFrameStyle.Declarations(kHtmlParserCore.DecodeEntities(TagText.substring(Found[2], Found[3]))); }
            let Put: string = "";
            if (Rule.length > 0) { Put = " " + AppFrameStyle.Attr + "=\"" + String(this.IndexOf(Rule)) + "\""; }
            this.Attrs = this.Attrs + 1;
            return this.Rewrite(TagText.substring(0, Found[0]) + Put + TagText.substring(Found[1]), false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Rewrite");
            return TagText;
        }
    };

    /** The number of a distinct declaration list - one rule each, however many elements share it. */
    private IndexOf = (Rule: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let At: number = this.Values.indexOf(Rule);
            if (At >= 0) { return At + 1; }
            this.Values.push(Rule);
            At = this.Values.length;
            this.Rules.push("[" + AppFrameStyle.Attr + "=\"" + String(At) + "\"]{" + Rule + "}");
            return At;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.IndexOf");
            return 0;
        }
    };

    private Result = (): AppFrameSplit => {
        if (getCoreUtils().KErrorMode === true) { return AppFrameSplit.Whole(this.Text); }
        try {
            const Slots: number[] = this.Slots.slice();
            const Sheets: string[] = this.Sheets.slice();
            if (this.Rules.length > 0 || this.Extra.length > 0) {
                // ⚠ No `</head>` (not a page of ours): the rules go at the very end - late, but never lost.
                if (this.RulesAt < 0) { this.RulesAt = this.Mark(); }
                Slots.push(this.RulesAt);
                Sheets.push([this.Extra].concat(this.Rules).join("\n"));
            }
            const Out: AppFrameSplit = new AppFrameSplit();
            let From: number = 0;
            // ⚠ In document order: a `<style>` in the BODY comes after the rules' place at `</head>`.
            for (const At of Slots.slice().sort(AppFrameStyle.Ascending)) {
                Out.Pieces.push(this.Parts.slice(From, At).join(""));
                Out.Sheets.push(Sheets[Slots.indexOf(At)]);
                From = At + 1;
            }
            Out.Pieces.push(this.Parts.slice(From).join(""));
            Out.Attrs = this.Attrs;
            Out.Blocks = this.Sheets.length;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameScan.Result");
            return AppFrameSplit.Whole(this.Text);
        }
    };
}

export class AppFrameStyle {
    /** What a `style` attribute becomes: `data-wac-s="N"`, matched by rule N of the page's sheet. */
    public static readonly Attr: string = "data-wac-s";

    /** A page with its styles taken out (see the header). */
    /**
     * A page with its styles taken out (see the header). `Extra` is the app's own CSS for the frame; it goes
     * into the last sheet, before the attribute rules - where the `<style>` elements the app used to add
     * after load sat in the cascade.
     */
    public static Split = (Html: string, Extra: string = ""): AppFrameSplit => {
        if (getCoreUtils().KErrorMode === true) { return AppFrameSplit.Whole(Html); }
        try {
            return new AppFrameScan(Html, Extra).Run();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.Split");
            return AppFrameSplit.Whole(Html);
        }
    };

    /**
     * The page again, each sheet linked at its place. `Hrefs` are ours (`blob:` URLs), one per sheet, in
     * order; a sheet with no href is simply left out (its styles do not apply - never an inline copy).
     */
    public static Join = (Split: AppFrameSplit, Hrefs: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return Split.Pieces.join(""); }
        try {
            const Out: string[] = [];
            for (let At: number = 0; At < Split.Pieces.length; At = At + 1) {
                Out.push(Split.Pieces[At]);
                if (At >= Split.Sheets.length || At >= Hrefs.length || nz(Hrefs[At]).length === 0) { continue; }
                Out.push("<link rel=\"stylesheet\" href=\"" + kHTMLCore.EscapeAttr(Hrefs[At]) + "\">");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.Join");
            return Split.Pieces.join("");
        }
    };

    /** DOCUMENTED EXCEPTION: a numeric sort comparer. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Ascending = (A: number, B: number): number => {
        return A - B;
    };

    /** Index of the `>` that ends the tag opened at `From`, skipping quoted attribute values; -1 = none. */
    public static TagEnd = (Text: string, From: number): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            let Last: string = "";
            for (let At: number = From + 1; At < Text.length; At = At + 1) {
                const Ch: string = Text.charAt(At);
                if (Ch === ">") { return At; }
                if ((Ch === "\"" || Ch === "'") && Last === "=") {
                    const Shut: number = Text.indexOf(Ch, At + 1);
                    if (Shut < 0) { return -1; }
                    At = Shut;
                }
                if (/\s/.test(Ch) === false) { Last = Ch; }
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.TagEnd");
            return -1;
        }
    };

    /** The lower-case tag name at the start of a start tag's inside ("div class=..." -> "div"). */
    public static NameOf = (Inside: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Found: RegExpExecArray | null = /^[A-Za-z][^\s\/>]*/.exec(Inside);
            if (Found === null) { return ""; }
            return Found[0].toLowerCase();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.NameOf");
            return "";
        }
    };

    /** Index of `</Name` (followed by a name end) from `From`, case-insensitive; -1 = none. */
    public static CloseOf = (Text: string, Name: string, From: number): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            const Lower: string = Text.toLowerCase();
            const Wanted: string = "</" + Name;
            let At: number = Lower.indexOf(Wanted, From);
            while (At >= 0) {
                if (/[\s\/>]/.test(Text.charAt(At + Wanted.length)) === true) { return At; }
                At = Lower.indexOf(Wanted, At + 1);
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.CloseOf");
            return -1;
        }
    };

    /**
     * The first `style` attribute of one start tag: [start of the whitespace before it, end of the attribute,
     * start of its value, end of its value] - or [] when there is none. Attributes are read the way the HTML
     * tokenizer reads them, so `title="a style=b"` is a title, not a style.
     */
    public static StyleAttr = (TagText: string): number[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Name: RegExpExecArray | null = /^<[A-Za-z][^\s\/>]*/.exec(TagText);
            if (Name === null) { return []; }
            // ⚠ STICKY (`y`): each attribute must start where the last one ended, or a search could land inside a value.
            // ⚠ A name may START with `=`, `"` or `'` (a parse error the browser still reads as a name), so a stray
            // character cannot stop the walk before a `style` further along.
            const Attr: RegExp = /(\s*)([^\s\/>][^\s\/>=]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?|\s*\//y;
            Attr.lastIndex = Name[0].length;
            let Found: RegExpExecArray | null = Attr.exec(TagText);
            while (Found !== null && Found[0].length > 0) {
                if (nz(Found[2]).toLowerCase() === "style") { return AppFrameStyle.Span(Found); }
                Found = Attr.exec(TagText);
            }
            return [];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.StyleAttr");
            return [];
        }
    };

    /** `StyleAttr`'s four offsets from one match of its attribute pattern. */
    private static Span = (Found: RegExpExecArray): number[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Start: number = Found.index;
            const End: number = Start + Found[0].length;
            let Value: string = "";
            let Quote: number = 0;
            if (Found[3] !== undefined) { Value = Found[3]; Quote = 1; }
            else if (Found[4] !== undefined) { Value = Found[4]; Quote = 1; }
            else if (Found[5] !== undefined) { Value = Found[5]; }
            const ValueEnd: number = End - Quote;
            return [Start, End, ValueEnd - Value.length, ValueEnd];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.Span");
            return [];
        }
    };

    /**
     * A `style` value as the body of a rule: each declaration marked `!important`, so it wins the way the
     * inline style did. "" when the value is empty or could break out of its rule (see the header).
     */
    public static Declarations = (Value: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Text: string = nz(Value);
            if (AppFrameStyle.Safe(Text) === false) { return ""; }
            const Out: string[] = [];
            for (const One of AppFrameStyle.Parts(Text)) {
                const Decl: string = One.trim();
                if (Decl.indexOf(":") <= 0) { continue; }
                if (/!\s*important$/i.test(Decl) === true) { Out.push(Decl); continue; }
                Out.push(Decl + " !important");
            }
            return Out.join(";");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.Declarations");
            return "";
        }
    };

    /** False for anything that could end the rule early or reach past it. */
    public static Safe = (Value: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (/[{}<\\\r\n]/.test(Value) === true) { return false; }
            let Depth: number = 0;
            let Quote: string = "";
            for (const Ch of Value) {
                if (Quote.length > 0) { if (Ch === Quote) { Quote = ""; } continue; }
                if (Ch === "\"" || Ch === "'") { Quote = Ch; continue; }
                if (Ch === "(") { Depth = Depth + 1; }
                if (Ch === ")") { Depth = Depth - 1; }
                if (Depth < 0) { return false; }
            }
            return Depth === 0 && Quote.length === 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.Safe");
            return false;
        }
    };

    /** The declarations of a value: split on `;` outside quotes and parentheses (`url("a;b")` is one). */
    public static Parts = (Value: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            let Depth: number = 0;
            let Quote: string = "";
            let Now: string = "";
            for (const Ch of nz(Value)) {
                if (Quote.length > 0) { Now = Now + Ch; if (Ch === Quote) { Quote = ""; } continue; }
                if (Ch === "\"" || Ch === "'") { Quote = Ch; }
                if (Ch === "(") { Depth = Depth + 1; }
                if (Ch === ")") { Depth = Depth - 1; }
                if (Ch === ";" && Depth === 0) { Out.push(Now); Now = ""; continue; }
                Now = Now + Ch;
            }
            Out.push(Now);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameStyle.Parts");
            return [];
        }
    };
}
